/**
 * IcpSynthesisRenderer (T-059, ARCH-005 §1/§9, TECH-09, AI-01).
 *
 * Renders the Zod-validated icp_synthesis payload: a persona summary box for
 * the primary audience, secondary audiences, and a distinct "who you are NOT
 * serving" callout block (T-059 AC). Body only.
 *
 * Verbosity (ARCH-005 §5): 'recommend' shows the persona summary + the
 * not-serving line only; 'show_me' adds pains/triggers/where + secondary
 * audiences + reasons; 'build_it_with_me' adds the methodology callout.
 */

import {
  FieldList,
  MethodologyCallout,
  SectionHead,
  type RendererProps,
} from '../shared';
import type { IcpSynthesisContent } from './payload';

export function IcpSynthesisRenderer({
  payload,
  verbosity,
}: RendererProps<IcpSynthesisContent>) {
  const full = verbosity !== 'recommend';
  return (
    <div className="space-y-6">
      <div className="rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-linen)]/30 p-4">
        <SectionHead>Primary audience</SectionHead>
        <p className="font-medium text-[color:var(--color-navy)]">
          {payload.primary_audience.label}
        </p>
        <p className="text-sm text-[color:var(--color-text-mute)]">
          {payload.primary_audience.summary}
        </p>
        {full ? (
          <div className="mt-3 grid gap-3 text-sm sm:grid-cols-3">
            <FieldList title="Pains" items={payload.primary_audience.pains} />
            <FieldList
              title="Triggers"
              items={payload.primary_audience.triggers}
            />
            <FieldList
              title="Where they are"
              items={payload.primary_audience.where_they_are}
            />
          </div>
        ) : null}
      </div>

      {full && payload.secondary_audiences.length > 0 ? (
        <div>
          <SectionHead>Secondary audiences</SectionHead>
          <ul className="space-y-2 text-sm">
            {payload.secondary_audiences.map((a, i) => (
              <li key={i}>
                <span className="font-medium text-[color:var(--color-navy)]">
                  {a.label}:{' '}
                </span>
                <span className="text-[color:var(--color-text-mute)]">
                  {a.summary}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="rounded-md border-l-2 border-[color:var(--color-rule)] bg-[color:var(--color-white)] p-4">
        <SectionHead>Who you are not serving</SectionHead>
        <p className="text-sm text-[color:var(--color-navy)]">
          {payload.not_serving.summary}
        </p>
        {full ? (
          <div className="mt-2">
            <FieldList title="Why" items={payload.not_serving.reasons} />
          </div>
        ) : null}
      </div>

      <MethodologyCallout verbosity={verbosity}>
        <p>
          The "not serving" block is the load-bearing one. A target audience
          that excludes no one is not a target. When you redo this, write the
          exclusion list first and see if the primary audience still survives
          it.
        </p>
      </MethodologyCallout>
    </div>
  );
}
